import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Divider,
  Button,
  Stack,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Star,
  Delete,
  Edit,
  Storefront,
  Person,
  FormatQuote,
} from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { SellerReview } from "../../../../services/sellerReviews";
import { sellerReviewService } from "../../../../services/sellerReviews";
 import ReviewEditDialog from "./ReviewEditDialog";
 import ReviewCommentBody from "../../../../components/ReviewCommentBody";
 import { ReviewCriteriaGrid } from "../../../../components/ReviewCriteriaRatings";

interface ReviewCardProps {
  review: SellerReview;
  onDelete: (id: string) => void;
  onEdit: (reviewId: string, updates: any) => Promise<void>;
}

const ReviewCard: React.FC<ReviewCardProps> = ({ review, onDelete, onEdit }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [commentExpanded, setCommentExpanded] = useState(false);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Use admin-supplied display_date if available, otherwise fall back to created_at
  const displayDate = review.display_date
    ? formatDate(review.display_date)
    : formatDate(review.created_at);

  const handleEditClick = () => {
    setEditDialogOpen(true);
  };

  const handleEditSave = async (reviewId: string, updates: any) => {
    await onEdit(reviewId, updates);
    setEditDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    try {
      const { error } = await sellerReviewService.deleteReview(review.id);
      if (error) throw error;
      onDelete(review.id);
      setDeleteDialogOpen(false);
    } catch (err: any) {
      console.error("Error deleting review:", err);
      alert(t("common.error", { defaultValue: "Failed to delete review" }));
    }
  };

  const sellerName =
    review.display_seller_name ||
    review.seller?.company_name ||
    review.seller?.full_name ||
    t("admin.sellerReviews.unknownSeller", { defaultValue: "Unknown Seller" });

  const reviewerName =
    review.display_reviewer_name ||
    review.reviewer?.full_name ||
    t("admin.sellerReviews.unknownReviewer", { defaultValue: "Unknown Reviewer" });

   return (
     <>
       <Card
         sx={{
           minHeight: 420,
           height: commentExpanded ? "auto" : 420,
           borderRadius: 4,
           border: "1px solid",
           borderColor: alpha(theme.palette.primary.main, 0.14),
           boxShadow: `0 14px 34px ${alpha(theme.palette.common.black, 0.08)}`,
           background: `linear-gradient(180deg, ${alpha(theme.palette.primary.light, 0.08)} 0%, ${theme.palette.background.paper} 28%)`,
           transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease, height 0.2s ease",
           overflow: commentExpanded ? "visible" : "hidden",
           "&:hover": {
             transform: "translateY(-4px)",
             boxShadow: `0 20px 44px ${alpha(theme.palette.common.black, 0.14)}`,
             borderColor: alpha(theme.palette.primary.main, 0.28),
           },
         }}
       >
     <CardContent sx={{ p: 3 }}>
       <Stack spacing={2.5}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
              <Avatar
                sx={{
                  width: 52,
                  height: 52,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: "primary.main",
                  fontWeight: 700,
                }}
              >
                <Storefront />
              </Avatar>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    color: "text.primary",
                    lineHeight: 1.2,
                    mb: 0.5,
                  }}
                >
                  {sellerName}
                </Typography>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={{ xs: 0.5, sm: 1 }}
                  divider={
                    <Box
                      component="span"
                      sx={{
                        width: 4,
                        height: 4,
                        borderRadius: "50%",
                        bgcolor: "text.disabled",
                        alignSelf: "center",
                      }}
                    />
                  }
                >
                  <Typography variant="body2" color="text.secondary">
                    {t("admin.reviewedBy", { defaultValue: "Reviewed by" })}: {reviewerName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {displayDate}
                  </Typography>
                </Stack>
              </Box>
            </Stack>

            <Chip
              icon={<Star sx={{ color: "#fff !important" }} />}
              label={`${review.overall_rating.toFixed(1)} / 5`}
              sx={{
                flexShrink: 0,
                height: 36,
                borderRadius: 999,
                px: 1,
                fontWeight: 800,
                color: theme.palette.common.white,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                boxShadow: `0 10px 24px ${alpha(theme.palette.primary.main, 0.28)}`,
              }}
            />
          </Box>

          <ReviewCriteriaGrid review={review} />

          {review.comment && (
            <Box
              sx={{
                position: "relative",
                p: 1.25,
                pl: 1.75,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.info.main, 0.05),
                border: "1px solid",
                borderColor: alpha(theme.palette.info.main, 0.12),
              }}
            >
              <FormatQuote
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  fontSize: 22,
                  color: alpha(theme.palette.info.main, 0.18),
                }}
              />
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Person sx={{ fontSize: 16, color: "info.main" }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: "0.78rem" }}>
                  {t("admin.sellerReviews.customerFeedback", { defaultValue: "Customer feedback" })}
                </Typography>
              </Stack>
              <ReviewCommentBody
                comment={review.comment}
                maxLines={4}
                onExpandedChange={setCommentExpanded}
                typographyProps={{
                  sx: {
                    fontSize: "0.78rem",
                    lineHeight: 1.35,
                    pr: 1,
                  },
                }}
                buttonSx={{ fontSize: "0.72rem" }}
              />
            </Box>
          )}

          <Divider />

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Button
              size="small"
              startIcon={<Edit />}
              color="primary"
              variant="outlined"
              onClick={handleEditClick}
              sx={{
                borderRadius: 999,
                px: 2,
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              {t("common.edit", { defaultValue: "Edit" })}
            </Button>

            <Button
              size="small"
              startIcon={<Delete />}
              color="error"
              variant="outlined"
              onClick={() => setDeleteDialogOpen(true)}
              sx={{
                borderRadius: 999,
                px: 2,
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              {t("common.delete", { defaultValue: "Delete" })}
            </Button>
          </Box>
         </Stack>
        </CardContent>
      </Card>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Typography variant="h6" fontWeight={700}>
              {t("common.delete", { defaultValue: "Delete" })} {t("admin.sellerReviews.review", { defaultValue: "Review" })}
            </Typography>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ py: 2 }}>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                {t("admin.sellerReviews.reviewFor", { defaultValue: "Review for" })}: <strong>{sellerName}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("common.deleteConfirm", { defaultValue: "Are you sure you want to delete this item? This action cannot be undone." })}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={() => setDeleteDialogOpen(false)}>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              sx={{
                borderRadius: 999,
                px: 3,
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              {t("common.delete", { defaultValue: "Delete" })}
            </Button>
          </DialogActions>
        </Dialog>

        <ReviewEditDialog
          review={review}
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          onSave={handleEditSave}
         />
       </>
     );
   };
   
   export default ReviewCard;